import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import TimeUtils from '@business/utils/time';
import { removeNullishValues } from '@business/utils/object';
import { parseUrlLanguage, parseUrlPropertyCode } from '@view/utils/urlParsers';
/**
 * Parses a URL parameter string to a positive integer
 * @param param - The URL parameter value (can be null or string)
 * @returns The parsed positive integer or undefined if invalid
 */
const parsePositiveInteger = (param) => {
    if (!param || param.trim() === '') {
        return undefined;
    }
    return /^\d+$/.test(param) ? Number(param) : undefined;
};
/**
 * Hook to get the App parameters from the URL using the search params
 * At this point, the URL should be rectified and legacy paramters converted as needed
 *
 * Important: This hook should be used only in the top level components given that it depends on useLocation and useParams
 * Do not use it for components that are deeply nested, used in Storybook or exported as webcomponents
 */
export const useAppParams = () => {
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams({});
    const urlLanguage = useMemo(() => {
        return parseUrlLanguage(pathname);
    }, [pathname]);
    const propertyCode = useMemo(() => {
        return parseUrlPropertyCode(pathname);
    }, [pathname]);
    const appParams = useMemo(() => {
        const code = searchParams.get('allotment_block_code') ||
            searchParams.get('promo') ||
            undefined;
        const guests = searchParams.get('guests')
            ? Number(searchParams.get('guests'))
            : undefined;
        const checkin = TimeUtils.fromIso(searchParams.get('checkin'), { startOfDay: true }) ||
            undefined;
        const checkout = TimeUtils.fromIso(searchParams.get('checkout'), { startOfDay: true }) ||
            undefined;
        const ratePlans = searchParams.get('rate_plan') || undefined;
        const accommodationTypes = searchParams.get('accommodation_types') || undefined;
        const amenities = searchParams.get('amenities') || undefined;
        const roomType = searchParams.get('room_type') || undefined;
        const roomIds = searchParams.get('rid') || undefined;
        const sort = searchParams.get('sort') || undefined;
        const adults = parsePositiveInteger(searchParams.get('adults'));
        const kids = parsePositiveInteger(searchParams.get('kids'));
        const urlCurrency = searchParams.get('currency') || undefined;
        const urlErrorCode = searchParams.get('error_code') || undefined;
        const urlErrorMessage = searchParams.get('error_message') || undefined;
        const redirectUrl = searchParams.get('redirect_url') || undefined;
        const dataRes = searchParams.get('data_res') || undefined;
        const utmSource = searchParams.get('utm_source') || undefined;
        const origin = searchParams.get('origin');
        const widgetSource = searchParams.get('widget_source') || undefined;
        const referralId = searchParams.get('referral_id') || undefined;
        const aiSource = searchParams.get('ai_source') || undefined;
        const cart = searchParams.get('cart') || undefined;
        // Click tracking parameters for Amplify attribution (tripadvisor, trivago, vio)
        const refid = searchParams.get('refid') || undefined;
        const trvReference = searchParams.get('trv_reference') || undefined;
        const redirectId = searchParams.get('redirect_id') || undefined;
        const country = searchParams.get('country') || undefined;
        const cpccode = searchParams.get('cpccode') || undefined;
        const googleHotelAds = {
            ghaAdvanceBookingWindow: searchParams.get('gha_advance_booking_window') || undefined,
            ghaCampaignId: searchParams.get('gha_campaign_id') || undefined,
            ghaGoogleSite: searchParams.get('gha_google_site') || undefined,
            ghaIsAdClick: searchParams.get('gha_is_ad_click') || undefined,
            ghaIsClickTypeHotel: searchParams.get('gha_is_click_type_hotel') || undefined,
            ghaIsClickTypeRoom: searchParams.get('gha_is_click_type_room') || undefined,
            ghaIsHotelCampaign: searchParams.get('gha_is_hotel_campaign') || undefined,
            ghaIsPromoted: searchParams.get('gha_is_promoted') || undefined,
            ghaLength: searchParams.get('gha_length') || undefined,
            ghaPaymentId: searchParams.get('gha_payment_id') || undefined,
            ghaPriceDisplayedTotal: searchParams.get('gha_price_displayed_total') || undefined,
            ghaPromoCode: searchParams.get('gha_promo_code') || undefined,
            ghaUserCountry: searchParams.get('gha_user_country') || undefined,
            ghaUserCurrency: searchParams.get('gha_user_currency') || undefined,
            ghaUserDevice: searchParams.get('gha_user_device') || undefined,
            ghaUserLanguage: searchParams.get('gha_user_language') || undefined,
            ghaUserListId: searchParams.get('gha_user_list_id') || undefined,
        };
        return removeNullishValues({
            accommodationTypes,
            adults,
            aiSource,
            amenities,
            cart,
            checkin,
            checkout,
            code,
            country,
            cpccode,
            dataRes,
            googleHotelAds,
            guests,
            kids,
            origin,
            propertyCode,
            ratePlans,
            redirectId,
            redirectUrl,
            referralId,
            refid,
            roomIds,
            roomType,
            sort,
            trvReference,
            urlCurrency,
            urlErrorCode,
            urlErrorMessage,
            urlLanguage,
            utmSource,
            widgetSource,
        });
    }, [propertyCode, searchParams, urlLanguage]);
    return appParams;
};
